import { useEffect, useMemo, useRef, useState } from 'react';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';

import useFilterLookups from '@/hooks/useFilterLookups';
import { trackSearch } from '@/hooks';
import { AI_IDS } from '../../ControlTower/types';
import type {
  ListItem,
  PaginatedConfig,
  PaginatedFetcher,
  SecondaryFilterOption,
  SubFilterMenuProps,
} from '../../ControlTower/types';
import { ALL_ALERT_IDS, sectionMap } from '../CustomAlertsFilterData';
import { useFilterPagination } from './useFilterPagination';
import {
  baseMenuStyle,
  SUBFILTER_FOOTER_HEIGHT,
  SUBFILTER_HEADER_EST,
  SUBFILTER_LOADING_MIN_HEIGHT,
  SUBFILTER_MENU_MAX_HEIGHT_CSS,
  SUBFILTER_MENU_MAX_HEIGHT_PX,
  SUBFILTER_SEARCH_EST,
} from '../utils/labels';

type UseSubFilterMenuStateArgs = Pick<
  SubFilterMenuProps,
  | 'filterBy'
  | 'loading'
  | 'secondaryFiltersDDL'
  | 'secondaryFiltersSource'
  | 'setSecondaryFiltersDDL'
  | 'setSecondaryFiltersSource'
  | 'appliedFilters'
  | 'setSelectedOptions'
  | 'savedFilterKey'
  | 'dropdownMaxHeight'
  | 'hiddenOptionIds'
  | 'initialItemsCount'
  | 'fetcherOverrides'
  | 'handleSecondaryFilterSelect'
  | 'handleApplyFilters'
  | 'onResetGroup'
  | 'setSecondFilterDisplay'
  | 'setIsFilterOpen'
>;

type Level = {
  id: string | number;
  display_name?: string;
  name?: string;
  service_level?: string;
};

type CourierOption = SecondaryFilterOption & {
  service_levels?: Level[];
};

type City = {
  id: string;
  city_name: string;
};

type State = {
  id: string;
  name: string;
  cities?: City[];
};

type BaseOption = {
  id: string;
  name: string;
};

type SelectedOption = {
  id: string;
  name: string;
};

const isPaginatedKey = (
  key: string,
  cfg: Readonly<Record<string, unknown>>
): key is
  | 'product_type'
  | 'locations'
  | 'from_location'
  | 'to_location'
  | 'origin_state'
  | 'destination_state' => key in cfg;

// Used while no paginated filter is open; the pagination hook is off then and
// never calls it.
const NO_PAGES_FETCHER: PaginatedFetcher<ListItem> = async () => ({
  data: [],
  pageCount: 0,
});

export function useSubFilterMenuState({
  filterBy,
  loading = false,
  secondaryFiltersDDL = [],
  secondaryFiltersSource = [],
  setSecondaryFiltersDDL,
  setSecondaryFiltersSource,
  appliedFilters = [],
  setSelectedOptions,
  savedFilterKey,
  dropdownMaxHeight,
  hiddenOptionIds,
  initialItemsCount,
  fetcherOverrides,
  handleSecondaryFilterSelect,
  handleApplyFilters,
  onResetGroup,
  setSecondFilterDisplay,
  setIsFilterOpen,
}: UseSubFilterMenuStateArgs) {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const initialListRef = useRef<SecondaryFilterOption[]>([]);
  const [initialCount, setInitialCount] = useState<number>(0);
  const userTouchedSelectionRef = useRef(false);

  // savedFilterKey is set by the page itself ('shipment' only for the real
  // Shipments page — 'ai_history' | 'inventory' | 'gxp_reports' | 'report'
  // reuse this same menu elsewhere) so it's a reliable, already-threaded
  // signal for whether this instance's frontend-only searches should be
  // tracked (see the trackSearch calls below). The paginated backend
  // lookups below are never audited from here.
  const auditSearch = savedFilterKey === 'shipment';
  const lookups = useFilterLookups();
  const resolvedMenuMaxHeight = useMemo(() => {
    if (typeof dropdownMaxHeight === 'number') return dropdownMaxHeight;
    if (typeof dropdownMaxHeight === 'string') return dropdownMaxHeight;
    return SUBFILTER_MENU_MAX_HEIGHT_CSS;
  }, [dropdownMaxHeight]);

  const resolvedMenuMaxHeightPx = useMemo(() => {
    if (typeof dropdownMaxHeight === 'number') return dropdownMaxHeight;
    return SUBFILTER_MENU_MAX_HEIGHT_PX;
  }, [dropdownMaxHeight]);

  const paginatedConfigs = useMemo(() => {
    const productTypeFetcher =
      fetcherOverrides?.product_type ?? lookups.fetchers?.product_type;
    const locationsFetcher =
      fetcherOverrides?.locations ?? lookups.fetchers?.locations;
    const inventoryLocationsFetcher = lookups.fetchers?.inventory_locations;
    const groupedLocationsFetcher = (fetcherOverrides?.grouped_locations ??
      lookups.fetchers?.grouped_locations) as
      NonNullable<typeof lookups.fetchers>['grouped_locations'] | undefined;

    return {
      product_type: productTypeFetcher
        ? ({
            enabled: true,
            fetcher: productTypeFetcher as PaginatedFetcher<ListItem>,
            limit: 50,
            mapToOption: (row: ListItem): SecondaryFilterOption => ({
              id: String(row.id),
              name: row.name,
              title: row.name,
            }),
          } satisfies PaginatedConfig<ListItem>)
        : null,
      locations: inventoryLocationsFetcher
        ? ({
            enabled: true,
            fetcher: inventoryLocationsFetcher as PaginatedFetcher<ListItem>,
            limit: 50,
            mapToOption: (row: ListItem): SecondaryFilterOption => ({
              id: String(row.id),
              name: row?.name ?? row?.title ?? '',
              title: row?.name ?? row?.title ?? '',
            }),
          } satisfies PaginatedConfig<ListItem>)
        : null,
      from_location: groupedLocationsFetcher
        ? {
            enabled: true,
            fetcher: groupedLocationsFetcher,
            limit: 50,
            filterType: 'origin',
            mapToOption: (row: ListItem): SecondaryFilterOption => ({
              id: String(row.id),
              name: row?.name ?? row?.title ?? '',
              title: row?.name ?? row?.title ?? '',
            }),
          }
        : null,
      to_location: groupedLocationsFetcher
        ? {
            enabled: true,
            fetcher: groupedLocationsFetcher,
            limit: 50,
            filterType: 'destination',
            mapToOption: (row: ListItem): SecondaryFilterOption => ({
              id: String(row.id),
              name: row?.name ?? row?.title ?? '',
              title: row?.name ?? row?.title ?? '',
            }),
          }
        : null,
      origin_state: locationsFetcher
        ? ({
            enabled: true,
            fetcher: locationsFetcher as PaginatedFetcher<ListItem>,
            limit: 50,
            mapToOption: (row: ListItem): SecondaryFilterOption => ({
              id: String(row.id),
              name: row?.name ?? row?.title ?? '',
              title: row?.name ?? row?.title ?? '',
            }),
          } satisfies PaginatedConfig<ListItem>)
        : null,
      destination_state: locationsFetcher
        ? ({
            enabled: true,
            fetcher: locationsFetcher as PaginatedFetcher<ListItem>,
            limit: 50,
            mapToOption: (row: ListItem): SecondaryFilterOption => ({
              id: String(row.id),
              name: row?.name ?? row?.title ?? '',
              title: row?.name ?? row?.title ?? '',
            }),
          } satisfies PaginatedConfig<ListItem>)
        : null,
    } as const;
  }, [lookups, fetcherOverrides]);

  const paginatedConfig = useMemo(() => {
    if (!paginatedConfigs) return null;
    return isPaginatedKey(filterBy, paginatedConfigs)
      ? (paginatedConfigs[filterBy] as PaginatedConfig<ListItem>)
      : null;
  }, [filterBy, paginatedConfigs]);

  const isPaginated = !!paginatedConfig?.enabled;
  const pg = useFilterPagination<ListItem>({
    enabled: isPaginated,
    fetcher: paginatedConfig?.fetcher ?? NO_PAGES_FETCHER,
    limit: paginatedConfig?.limit ?? 50,
    debounceMs: 500,
    filterType: paginatedConfig?.filterType,
  });

  const isGroupedLocations =
    filterBy === 'from_location' ||
    filterBy === 'to_location' ||
    filterBy === 'origin_state' ||
    filterBy === 'destination_state';

  const paginatedOptions: SecondaryFilterOption[] = useMemo(() => {
    if (!isPaginated || !paginatedConfig) return [];
    if (isGroupedLocations) return [];
    return (pg.data ?? []).map(paginatedConfig.mapToOption);
  }, [isPaginated, paginatedConfig, pg.data, isGroupedLocations]);

  const rawListToRender: SecondaryFilterOption[] = isPaginated
    ? paginatedOptions
    : secondaryFiltersDDL || [];

  const listToRender =
    savedFilterKey === 'shipment'
      ? rawListToRender.filter(
          (option) => !hiddenOptionIds?.has(String(option.id))
        )
      : rawListToRender;

  const menuStyle = useMemo<React.CSSProperties>(() => {
    const loadingMinHeight = loading
      ? SUBFILTER_HEADER_EST + SUBFILTER_LOADING_MIN_HEIGHT
      : undefined;

    if (!isPaginated) {
      return {
        ...baseMenuStyle,
        maxHeight: resolvedMenuMaxHeight,
        minHeight: loadingMinHeight,
        overflowY: 'auto',
        paddingBottom: filterBy === 'saved_filters' ? '8px' : '0px',
      };
    }

    return {
      ...baseMenuStyle,
      maxHeight: resolvedMenuMaxHeight,
      minHeight: loadingMinHeight,
      overflowY: 'hidden',
      paddingBottom: 0,
    };
  }, [isPaginated, filterBy, resolvedMenuMaxHeight, loading]);

  useEffect(() => {
    if (isPaginated) {
      setIsFocused(false);
      if (userTouchedSelectionRef.current) return;

      const seeded = (appliedFilters || [])
        .filter((a) => a.filterBy === filterBy || a.dateFilter === filterBy)
        .map((a) => ({ id: a.id, name: a.name ?? a.title ?? String(a.id) }));

      setSelectedOptions?.(seeded);
      return;
    }

    setSearchQuery('');
    setIsFocused(false);
    userTouchedSelectionRef.current = false;

    if (filterBy === 'alert_types') {
      const appliedAlerts = (appliedFilters || []).filter(
        (a) => a.filterBy === 'alert_types' || ALL_ALERT_IDS.has(a.id)
      );
      setSelectedOptions?.(
        appliedAlerts.length > 0
          ? appliedAlerts.map((a) => ({
              id: a.id,
              name: a.name || a.title || a.id,
            }))
          : [{ id: 'open', name: 'Open' }]
      );
      return;
    }

    if (filterBy === 'courier') {
      const couriers: CourierOption[] = Array.isArray(secondaryFiltersSource)
        ? (secondaryFiltersSource as CourierOption[])
        : [];
      const courierById = new Map<string, CourierOption>();
      const slById = new Map<
        string | number,
        { sl: Level; courier: CourierOption }
      >();

      couriers.forEach((courier) => {
        courierById.set(String(courier.id), courier);
        (courier.service_levels || []).forEach((sl) => {
          slById.set(sl.id, { sl, courier });
        });
      });

      const idsAppliedHere = new Set(
        (appliedFilters || [])
          .filter((a) => a.filterBy === 'courier')
          .map((a) => a.id)
      );
      const selectedCourierIds: string[] = [];
      const selectedSLIds: Array<string | number> = [];

      idsAppliedHere.forEach((id) => {
        if (courierById.has(String(id))) selectedCourierIds.push(String(id));
        else if (slById.has(id)) selectedSLIds.push(id);
      });

      const selectedFromParents = selectedCourierIds.map((cid) => {
        const courier = courierById.get(cid)!;
        const chosenLevels = (courier.service_levels || []).map((sl) => ({
          id: String(sl.id),
          name: sl.display_name || sl.name || sl.service_level || String(sl.id),
        }));
        return {
          id: String(courier.id),
          name: courier.name || courier.title || courier.courier_name || '',
          service_levels: chosenLevels,
        };
      });

      const loneChildren = selectedSLIds
        .map((slid) => slById.get(slid))
        .filter(
          (rec): rec is { sl: Level; courier: CourierOption } =>
            !!rec && !selectedCourierIds.includes(String(rec.courier.id))
        )
        .map((rec) => ({
          id: String(rec.sl.id),
          name:
            rec.sl.display_name ||
            rec.sl.name ||
            rec.sl.service_level ||
            String(rec.sl.id),
        }));

      setSelectedOptions?.([...selectedFromParents, ...loneChildren]);
      initialListRef.current = couriers;
      setInitialCount(couriers.length);
      setSecondaryFiltersDDL?.(couriers);
      return;
    }

    const base =
      Array.isArray(secondaryFiltersSource) && secondaryFiltersSource.length
        ? secondaryFiltersSource
        : [];

    initialListRef.current = base;
    setInitialCount(base.length);
    setSecondaryFiltersDDL?.(base);

    const idsAppliedHere = new Set(
      (appliedFilters || [])
        .filter((a) => a.filterBy === filterBy || a.dateFilter === filterBy)
        .map((a) => a.id)
    );
    let seeded: SelectedOption[] = [];

    if (filterBy === 'origin_state' || filterBy === 'destination_state') {
      (base as State[]).forEach((state) => {
        if (idsAppliedHere.has(state.id)) {
          seeded.push({ id: state.id, name: state.name });
        }
        state.cities?.forEach((city) => {
          if (idsAppliedHere.has(city.id)) {
            seeded.push({ id: city.id, name: city.city_name });
          }
        });
      });
    } else {
      seeded = (base as BaseOption[]).filter((opt) =>
        idsAppliedHere.has(opt.id)
      );
    }

    setSelectedOptions?.(
      filterBy === 'alert_assignment'
        ? Array.from(new Map(seeded.map((item) => [item.id, item])).values())
        : seeded
    );
  }, [
    appliedFilters,
    filterBy,
    secondaryFiltersSource,
    setSecondaryFiltersDDL,
    setSelectedOptions,
    isPaginated,
  ]);

  useEffect(() => {
    if (!setSecondaryFiltersDDL) return;
    if (isPaginated) return;

    const q = searchQuery.trim();
    const base = initialListRef.current || [];

    if (!q) {
      setSecondaryFiltersDDL(base);
      setSecondaryFiltersSource?.(base);
      return;
    }

    // These seven are filtered client-side (the full list is already loaded),
    // so there's no network request for auditSearch to hook into — track the
    // search directly here instead, only for the real Shipments page.
    if (
      auditSearch &&
      (filterBy === 'team' ||
        filterBy === 'courier' ||
        filterBy === 'product' ||
        filterBy === 'alert_types' ||
        filterBy === 'project_id' ||
        filterBy === 'saved_filters' ||
        filterBy === 'location_type')
    ) {
      trackSearch(q);
    }

    const lower = q.toLowerCase();
    const filtered = base.filter((option) => {
      const text =
        option?.title ||
        option?.name ||
        option?.courier_name ||
        option?.label ||
        option?.value ||
        '';
      return String(text).toLowerCase().includes(lower);
    });
    setSecondaryFiltersDDL(filtered);
  }, [
    searchQuery,
    setSecondaryFiltersDDL,
    setSecondaryFiltersSource,
    isPaginated,
    auditSearch,
    filterBy,
  ]);

  const alertsTotal =
    (sectionMap?.alert_state?.length || 0) +
    (sectionMap?.critical_alerts?.length || 0) +
    (sectionMap?.at_risk_alerts?.length || 0) +
    AI_IDS.length;
  const baseCount = initialItemsCount ?? initialCount;
  const showSearchBar = isPaginated
    ? true
    : filterBy === 'saved_filters'
      ? baseCount > 10 || isFocused || searchQuery.length > 0
      : filterBy === 'alert_types'
        ? alertsTotal > 10 || isFocused || searchQuery.length > 0
        : filterBy === 'courier' || filterBy === 'alert_assignment'
          ? true
          : baseCount > 10 || isFocused || searchQuery.length > 0;

  const paginatedListHeight = useMemo(() => {
    if (typeof resolvedMenuMaxHeightPx !== 'number') {
      return Math.max(
        120,
        SUBFILTER_MENU_MAX_HEIGHT_PX -
          SUBFILTER_HEADER_EST -
          (showSearchBar ? SUBFILTER_SEARCH_EST : 0) -
          (filterBy === 'saved_filters' ? 0 : SUBFILTER_FOOTER_HEIGHT)
      );
    }

    const searchH = showSearchBar ? SUBFILTER_SEARCH_EST : 0;
    const footerH = filterBy === 'saved_filters' ? 0 : SUBFILTER_FOOTER_HEIGHT;
    return Math.max(
      120,
      resolvedMenuMaxHeightPx - SUBFILTER_HEADER_EST - searchH - footerH
    );
  }, [showSearchBar, filterBy, resolvedMenuMaxHeightPx]);

  const handleCheckboxChange = (
    event: CheckboxChangeEvent,
    option: SecondaryFilterOption
  ) => {
    userTouchedSelectionRef.current = true;
    handleSecondaryFilterSelect?.(event, option);
  };

  const handleReset = () => {
    userTouchedSelectionRef.current = false;

    if (isPaginated) {
      pg.reset('');
      setSearchQuery('');
    }

    setSelectedOptions?.([]);
    onResetGroup?.();
    setSecondFilterDisplay(false);
    setIsFilterOpen(false);
  };

  const handleApply = () => {
    handleApplyFilters?.();
    setSecondFilterDisplay(false);
    setIsFilterOpen(false);
  };

  return {
    searchQuery,
    setSearchQuery,
    showSearchBar,
    isPaginated,
    isGroupedLocations,
    listToRender,
    menuStyle,
    paginatedListHeight,
    pg,
    handleCheckboxChange,
    handleReset,
    handleApply,
  };
}
